// Market order statuses — presentation, the buyer's seven steps, and the
// admin's next-step buttons (HioBuy marketplace). The database is the
// authority: public.market_transition_allowed() decides every move and
// public.market_customer_step() maps statuses to steps;
// scripts/hiobuy/local-stack/sql.test.mjs fails if NEXT or customerStep here
// ever disagrees with them.

export const ORDER_STATUSES = ['awaiting_shipping_quote', 'pending_payment', 'paid', 'procuring', 'purchased', 'in_transit_to_warehouse',
  'at_warehouse', 'inspected', 'packed', 'shipped', 'delivered', 'completed', 'disputed', 'refund_pending', 'refunded', 'cancelled', 'expired'];

// Internal status names — for the admin console.
export const STATUS_LABEL = {
  ar: { awaiting_shipping_quote: 'بانتظار تسعير الشحن', pending_payment: 'بانتظار الدفع', paid: 'مدفوع — بانتظار المراجعة', procuring: 'جارٍ الشراء من المورد',
    purchased: 'تم الشراء', in_transit_to_warehouse: 'في الطريق إلى المستودع', at_warehouse: 'وصل إلى المستودع', inspected: 'تم الفحص', packed: 'جاهز للشحن',
    shipped: 'تم الشحن', delivered: 'تم التسليم', completed: 'مكتمل', disputed: 'قيد المراجعة', refund_pending: 'جارٍ الاسترداد', refunded: 'تم الاسترداد',
    cancelled: 'ملغى', expired: 'انتهت مهلة الدفع' },
  en: { awaiting_shipping_quote: 'Awaiting shipping price', pending_payment: 'Awaiting payment', paid: 'Paid — awaiting review', procuring: 'Buying from the supplier',
    purchased: 'Purchased', in_transit_to_warehouse: 'On the way to the warehouse', at_warehouse: 'At the warehouse', inspected: 'Inspected', packed: 'Ready to ship',
    shipped: 'Shipped', delivered: 'Delivered', completed: 'Completed', disputed: 'Under review', refund_pending: 'Refund in progress', refunded: 'Refunded',
    cancelled: 'Cancelled', expired: 'Payment window expired' },
  zh: { awaiting_shipping_quote: '待报运费', pending_payment: '待付款', paid: '已付款—待审核', procuring: '采购中', purchased: '已采购', in_transit_to_warehouse: '运往仓库中',
    at_warehouse: '已到仓库', inspected: '已验货', packed: '待发货', shipped: '已发货', delivered: '已送达', completed: '已完成', disputed: '处理中',
    refund_pending: '退款中', refunded: '已退款', cancelled: '已取消', expired: '付款超时' },
};
export const statusLabel = (s, lang) => (STATUS_LABEL[lang] || STATUS_LABEL.ar)[s] || s;

// ── What the BUYER sees: seven steps, real events only ──────────────────────
// Same mapping as public.market_customer_step(). A payment is "received" —
// never "purchased from the supplier".
export const CUSTOMER_STEPS = ['received', 'processing', 'purchased', 'warehouse', 'preparing', 'shipped', 'delivered'];
const STEP_OF = {
  paid: 'received', procuring: 'processing', purchased: 'purchased', in_transit_to_warehouse: 'purchased',
  at_warehouse: 'warehouse', inspected: 'warehouse', packed: 'preparing', shipped: 'shipped', delivered: 'delivered', completed: 'delivered',
};
export const customerStep = (status) => STEP_OF[status] ?? status;

export const STEP_LABEL = {
  ar: { received: 'تم استلام الطلب', processing: 'قيد المعالجة', purchased: 'تم الشراء', warehouse: 'في المستودع', preparing: 'تجهيز الشحنة',
    shipped: 'تم الشحن', delivered: 'تم التسليم', awaiting_shipping_quote: 'نحسب تكلفة الشحن', pending_payment: 'بانتظار الدفع',
    disputed: 'قيد المراجعة', refund_pending: 'جارٍ استرداد كامل المبلغ', refunded: 'تم استرداد كامل المبلغ', cancelled: 'ملغى', expired: 'انتهت مهلة الدفع' },
  en: { received: 'Order received', processing: 'Processing', purchased: 'Purchased', warehouse: 'At the warehouse', preparing: 'Preparing shipment',
    shipped: 'Shipped', delivered: 'Delivered', awaiting_shipping_quote: 'Pricing your shipping', pending_payment: 'Awaiting payment',
    disputed: 'Under review', refund_pending: 'Full refund in progress', refunded: 'Refunded in full', cancelled: 'Cancelled', expired: 'Payment window expired' },
  zh: { received: '已收到订单', processing: '处理中', purchased: '已采购', warehouse: '仓库处理中', preparing: '准备发货',
    shipped: '已发货', delivered: '已送达', awaiting_shipping_quote: '正在核算运费', pending_payment: '待付款',
    disputed: '处理中', refund_pending: '全额退款处理中', refunded: '已全额退款', cancelled: '已取消', expired: '付款超时' },
};
export const stepLabel = (step, lang) => (STEP_LABEL[lang] || STEP_LABEL.ar)[step] || step;

// ── Admin: plain transitions (admin_market_order_transition) ────────────────
// Mirrors public.market_transition_allowed MINUS the moves that have their own
// guarded RPC: paid (verified payment only), pending_payment (staff shipping
// price), procuring (approve procurement), purchased (confirm purchase),
// refund_pending (full-refund request), refunded (provider-confirmed refund).
export const GUARDED_TARGETS = ['paid', 'pending_payment', 'procuring', 'purchased', 'refund_pending', 'refunded'];
export const NEXT = {
  awaiting_shipping_quote: ['cancelled'],
  pending_payment: ['cancelled', 'expired'],
  paid: [],
  procuring: [],
  purchased: ['in_transit_to_warehouse'],
  in_transit_to_warehouse: ['at_warehouse'],
  at_warehouse: ['inspected', 'packed'],
  inspected: ['packed'],
  packed: ['shipped'],
  shipped: ['delivered', 'disputed'],
  delivered: ['completed', 'disputed'],
  disputed: ['completed'],
  refund_pending: [],
};
// Statuses a full refund can be requested from (transition to refund_pending allowed).
export const REFUNDABLE = ['paid', 'procuring', 'purchased', 'in_transit_to_warehouse', 'at_warehouse', 'inspected', 'packed', 'disputed'];
